import { AVAILABILITY_STATUS } from '../../../shared/constants/availabilityStatus.js'
import { VISIBILITY_STATUS } from '../../../shared/constants/visibilityStatus.js'

/**
 * Parses the building_id, tier_id and ref_tier from an `id_cad` string.
 *
 * CAD identifier format: `{buildingCode}-{gate}-{tierCode}-{typeCode}-{unitOrdinal}`
 * Example: `'0V-01-04-M-036'` → building_id: '0V', tier_id: '04', ref_tier: 4
 *
 * @param {string|null|undefined} idCad
 * @returns {{ building_id: string|null, tier_id: string|null, ref_tier: number|null }}
 */
function parseIdCad(idCad) {
  if (!idCad || typeof idCad !== 'string') {
    return { building_id: null, tier_id: null, ref_tier: null }
  }
  const parts = idCad.split('-')
  const building_id = parts[0] ?? null
  const tier_id = parts[2] ?? null
  const ref_tier = tier_id !== null ? parseInt(tier_id, 10) : null
  return {
    building_id,
    tier_id,
    ref_tier: Number.isNaN(ref_tier) ? null : ref_tier,
  }
}

/**
 * Normalizes a raw product entry from the products endpoint or `apartments.json`.
 *
 * Both data sources share the same product shape. Fields derived from `id_cad`
 * (`building_id`, `tier_id`, `ref_tier`) are applied only when absent from the
 * raw payload — existing values are never overwritten. Runtime defaults for
 * `status` and `visibility` are applied in the same way.
 *
 * @param {object} raw - A single raw product object.
 * @returns {LocalProduct} Normalized product with all expected fields populated.
 */
export function normalizeProduct(raw) {
  const derived = parseIdCad(raw.id_cad)

  return {
    ...raw,
    building_id: raw.building_id ?? derived.building_id,
    tier_id:     raw.tier_id     ?? derived.tier_id,
    ref_tier:    raw.ref_tier    ?? derived.ref_tier,
    status:     raw.status     ?? AVAILABILITY_STATUS.UNKNOWN,
    visibility: raw.visibility ?? VISIBILITY_STATUS.VISIBLE,
  }
}
